import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
  Switch,
  Link,
  Collapse,
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import { Voyageurs } from '../models/Voyageurs';
import { API_BASE_URL } from '../config/api';
import { isLegrandUser } from '../utils/userUtils';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
}

const FormIndeECard: React.FC = () => {
  // Récupération des paramètres passés via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États issus des paramètres ou initialisés à des valeurs par défaut
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  // Champs de base
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');

  // Champs spécifiques au formulaire e-Card Inde
  const [dateArriveeInde, setDateArriveeInde] = useState<string>('');
  const [butVoyage, setButVoyage] = useState<string>('');
  const [adresseHotel, setAdresseHotel] = useState<string>('');
  const [telephoneUrgence, setTelephoneUrgence] = useState<string>('');

  // Champs liés aux voyageurs
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [showConditions, setShowConditions] = useState<boolean>(false);
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFeesForLegrand] = useState<boolean>(isLegrandUser());

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Options pour le combo But du voyage
  const butsVoyage = [
    'Tourisme',
    'Affaires',
    'Médical',
    'Ayush',
    'Conférence',
    'Recherche',
    'Études',
    'Emploi',
    'Officiel/Diplomatique',
    'Autre'
  ];

  // Calcul du total
  const total = prixconsulat + prixintervention;

  // Validation du formulaire
  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);
    const arriveeIndeDate = parseDate(dateArriveeInde);

    const errors: string[] = [];

    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push("La date de départ doit être une date valide et postérieure à aujourd'hui.");
      }
    } else {
      errors.push("La date de départ est requise.");
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push("La date de retour doit être une date valide et postérieure à la date de départ.");
      }
    } else if (dateSortie && !sortieDate) {
      errors.push("La date de retour est requise et doit être au format valide.");
    }

    // Date d'arrivée en Inde facultative (si différente de la date de départ), contrôlée seulement si saisie
    if (dateArriveeInde.trim()) {
      if (!arriveeIndeDate || isNaN(arriveeIndeDate.getTime()) || arriveeIndeDate <= today) {
        errors.push("La date d'arrivée en Inde doit être une date valide et postérieure à aujourd'hui.");
      } else if (departDate && arriveeIndeDate < departDate) {
        errors.push("La date d'arrivée en Inde ne peut pas être antérieure à la date de départ.");
      }
    }

    if (!selectedVoyageur) errors.push('Voyageur non sélectionné');
    if (!dateDepart.trim()) errors.push('Date de départ');
    if (!dateSortie.trim()) errors.push('Date de retour');
    if (!butVoyage.trim()) errors.push('But du voyage');
    if (!adresseHotel.trim()) errors.push('Adresse hôtel en Inde');
    if (!telephoneUrgence.trim()) errors.push("Numéro de téléphone d'un contact d'urgence");
    if (!sliderValue) errors.push("Engagement sur les conditions générales de vente");

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
  }, [dateDepart, dateSortie, sliderValue, selectedVoyageur, dateArriveeInde, butVoyage, adresseHotel, telephoneUrgence]);

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture de l'onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (
      window.confirm(
        'Les informations saisies ne sont pas enregistrées. Êtes-vous sûr de quitter ce formulaire ?'
      )
    ) {
      navigate(-1);
    }
  };

  // Fonction pour formatter la date en ajoutant automatiquement des slashs (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Soumission du formulaire via axios et redirection après succès
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }
      const etatFormulaire = champsNonRemplis.length === 0;
      const data = {
        etatFormulaire: etatFormulaire,
        idUtilisateur: userId,
        idVoyageur: selectedVoyageur,
        titreVisa: title,
        dateDepart: dateDepart,
        dateSortie: dateSortie,
        validite: validite,
        assuranceVisa: 'non',
        prixconsulat: prixconsulat,
        prixintervention: prixintervention,
        dateArriveeInde: dateArriveeInde,
        butVoyage: butVoyage,
        adresseHotel: adresseHotel,
        telephoneUrgence: telephoneUrgence,
      };

      const response = await axios.post(`${API_BASE_URL}/validerDemande`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error("Erreur lors de la validation de la demande:", error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setDateArriveeInde('');
    setButVoyage('');
    setAdresseHotel('');
    setTelephoneUrgence('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setSliderValue(false);
  };

  // Fonction vide si bouton désactivé
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt="Flag"
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              Demande enregistree avec succes !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Votre demande de visa pour {title} a bien ete enregistree.
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société et hors Legrand) */}
              {isClientSociete === false && !hideFeesForLegrand && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  Payer en ligne
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                Creer une nouvelle demande
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                Voir mes demandes
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate autoComplete="off" sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          {/* En-tête du formulaire */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box component="img" src={urlFlag} alt="Flag" sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }} />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              Formulaire pour {title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              Retour
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur (si non en mode édition) */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">Sélectionner un voyageur</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label="Sélectionner un voyageur"
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? ` - Passeport: ${v.numeropax}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1565c0'
                    }
                  }}
                >
                  Créer un nouveau voyageur
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt="Avatar"
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* Saisie des dates */}
          <TextField
            label="Date de départ de votre voyage (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Date d'arrivée en Inde (si différente de date de départ)"
            placeholder="jj/mm/aaaa"
            value={dateArriveeInde}
            onChange={(e) => setDateArriveeInde(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Date de retour de votre voyage (jj/mm/aaaa)"
            placeholder="jj/mm/aaaa"
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Champs spécifiques e-Card Inde */}
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="but-voyage-label">But du voyage</InputLabel>
            <Select
              labelId="but-voyage-label"
              id="but-voyage-select"
              value={butVoyage}
              label="But du voyage"
              onChange={(e: SelectChangeEvent) => setButVoyage(e.target.value)}
            >
              {butsVoyage.map((but) => (
                <MenuItem key={but} value={but}>
                  {but}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Adresse de votre hôtel en Inde"
            placeholder="Adresse complète de l'hôtel"
            value={adresseHotel}
            onChange={(e) => setAdresseHotel(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />

          <TextField
            label="Numéro de téléphone d'un contact d'urgence"
            placeholder="Ex: +33 6 12 34 56 78"
            value={telephoneUrgence}
            onChange={(e) => setTelephoneUrgence(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Section Conditions Générales de Vente */}
          <Box sx={{ my: 3, p: 2 }}>
            <Collapse in={showConditions}>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f9f9f9',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Conditions générales de vente
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ textAlign: 'justify', whiteSpace: 'pre-line', mb: 2 }}
                >
                  {`Formulaire fourni par Visa Monde et nécessairement rempli et retourné à Visa Monde par le Client lors de sa demande de Prestation.
Ce formulaire peut être un Bon de Commande Visa si le Client a recours à Visa Monde pour une demande de visa, ou un Bon de Commande Légalisation,
si le Client a recours à Visa Monde pour une demande de légalisation d'un document.

L'envoi du Bon de commande par le Client à Visa Monde vaut acceptation de l'offre de Prestation et engagement de la payer.

Dans tous les cas, le Client devra nécessairement remplir et signer un formulaire nommé Bon de Commande Visa en cas de demande de visa
et un Bon de Commande Légalisation en cas de demande de légalisation.

Ces deux documents sont désignés ci-après sous la dénomination unique « Bon de Commande ».

Le Bon de Commande devra être remis à Visa Monde soit directement au guichet (lorsque la commande est faite au guichet),
soit par courrier postal ou pli déposé par porteur.

Aucune demande de Prestation ne sera traitée sans remise de Bon de Commande intégralement rempli et dûment signé.
Les informations renseignées par le Client sur le Bon de Commande déterminent la Prestation sollicitée par le Client
et conditionnent la demande de visa ou de légalisation de documents que transmettra Visa Monde auprès des Autorités Compétentes.

Visa Monde constitue le dossier du Client uniquement au regard des informations reportées sur le Bon de Commande.
En aucun cas, Visa Monde ne vérifie l'exactitude des informations reportées sur le Bon de Commande, quand bien même d'autres documents
fournis par le Client viendraient les contredire.

Il appartient donc au Client de fournir des informations sincères, exactes et complètes. En cas de demande de visa, il doit notamment indiquer la date
d'entrée et de sortie de l'ensemble des Etats qu'il traversera au cours de son voyage, y compris les Etats dans lesquels il ne ferait que transiter.
Le Client doit s'assurer que l'identité renseignée sur le Bon de Commande est conforme à celle de son ou ses documents de voyage.

En cas de demande de légalisation de documents, le Client doit renseigner le Bon de commande et la date limite
à laquelle il souhaite recevoir les documents légalisés.

La remise du Bon de commande par le Client à Visa Monde vaut acceptation de l'offre de Prestation, du tarif de la Prestation et des présentes conditions générales de ventes.

Le Client s'engage à communiquer des coordonnées à jour et à informer Visa Monde sans délai de tout changement survenu depuis l'envoi du Bon de Commande.`}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setShowConditions(false)}
                  sx={{ display: 'block', mx: 'auto' }}
                  type="button"
                >
                  Fermer
                </Button>
              </Box>
            </Collapse>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                En validant, j'accepte les{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body1"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setShowConditions(true);
                  }}
                  sx={{ textDecoration: 'underline', color: '#0645AD' }}
                >
                  conditions générales de ventes
                </Link>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Switch
                  checked={sliderValue}
                  onChange={(e) => setSliderValue(e.target.checked)}
                  color="primary"
                />
                <Typography variant="body1" sx={{ ml: 1 }}>
                  Oui
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Récapitulatif des frais */}
          {!hideFeesForLegrand && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">Frais de visa : {prixconsulat}</Typography>
              <Typography variant="body1">Frais de service : {prixintervention}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Total : {total}
              </Typography>
            </Box>
          )}

          {/* État du formulaire */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  Formulaire incomplet.
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">Formulaire complet</Typography>
            )}
          </Box>

          {/* Bouton de soumission */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                Valider ma demande
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormIndeECard;
